import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { CssBaseline, LinearProgress, ThemeProvider } from '@mui/material';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { identifyAuth } from './analytics';
import theme from './theme';

const HomePage = lazy(() => import('./pages/HomePage'));
const EditHousehold = lazy(() => import('./components/EditHousehold'));
const MyDashboard = lazy(() => import('./pages/MyDashboard'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const Navbar = lazy(() => import('./components/Navbar'));
const Footer = lazy(() => import('./components/Footer'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const NiftyStuff = lazy(() => import('./pages/NiftyStuff'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const MyGoalsPage = lazy(() => import('./pages/MyGoalsPage'));
const BestBudgetingApps = lazy(() => import('./pages/pseopages/BestBudgetingApps'));
const BestMoneyManagementApps = lazy(() => import('./pages/pseopages/BestMoneyManagementApps'));
const BudgetForCouples = lazy(() => import('./pages/pseopages/BudgetForCouples'));
const BestNetWorthTracker = lazy(() => import('./pages/pseopages/BestNetWorthTracker'));
const FreeBudgetApp = lazy(() => import('./pages/pseopages/FreeBudgetApp'));
const WealthBuildingApp = lazy(() => import('./pages/pseopages/WealthBuildingApp'));
const DebtPayoffTracker = lazy(() => import('./pages/pseopages/DebtPayoffTracker'));
const FamilyFinanceApp = lazy(() => import('./pages/pseopages/FamilyFinanceApp'));
const WeeklyBudgetTracker = lazy(() => import('./pages/pseopages/WeeklyBudgetTracker'));
const SavingsGoalTracker = lazy(() => import('./pages/pseopages/SavingsGoalTracker'));
const BestWealthHabitBuilder = lazy(() => import('./pages/pseopages/BestWealthHabitBuilder'));
const ManualExpenseTrackerApp = lazy(() => import('./pages/pseopages/ManualExpenseTrackerApp'));
const Publications = lazy(() => import('./pages/publications'));

function RouteProgress() {
  return (
    <div className="nf-route-loader" role="status" aria-live="polite">
      <LinearProgress />
      <span>Loading Nifty Finance…</span>
    </div>
  );
}

function ProtectedRoute({ authReady, user, children }) {
  if (!authReady) return <RouteProgress />;
  return user ? children : <Navigate to="/" replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

function AppRoutes() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => onAuthStateChanged(auth, (firebaseUser) => {
    setUser(firebaseUser);
    setAuthReady(true);
    if (firebaseUser) identifyAuth(firebaseUser);
  }), []);

  const protect = (element) => (
    <ProtectedRoute authReady={authReady} user={user}>{element}</ProtectedRoute>
  );

  return (
    <div className="nf-app-shell">
      <a className="nf-skip-link" href="#main-content">Skip to main content</a>
      <ScrollToTop />
      <Navbar user={user} authReady={authReady} />
      <div id="main-content" className="nf-route-content">
        <Suspense fallback={<RouteProgress />}>
          <Routes>
            <Route path="/" element={<HomePage user={user} setUser={setUser} />} />
            <Route path="/EditHousehold" element={protect(<EditHousehold user={user} setIntakeCompleted={() => {}} />)} />
            <Route path="/account" element={protect(<AccountPage user={user} />)} />
            <Route path="/MyDashboard" element={protect(<MyDashboard user={user} />)} />
            <Route path="/MyGoalsPage" element={protect(<MyGoalsPage user={user} />)} />
            <Route path="/NiftyStuff" element={protect(<NiftyStuff user={user} />)} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/contactus" element={<ContactUs />} />
            <Route path="/publications" element={<Publications />} />
            <Route path="/publications/best-budgeting-apps" element={<BestBudgetingApps />} />
            <Route path="/publications/budget-apps-for-couples" element={<BudgetForCouples />} />
            <Route path="/publications/manual-expense-tracker-app" element={<ManualExpenseTrackerApp />} />
            <Route path="/publications/best-money-management-apps" element={<BestMoneyManagementApps />} />
            <Route path="/publications/best-net-worth-tracker-app" element={<BestNetWorthTracker />} />
            <Route path="/publications/best-wealth-habit-builder" element={<BestWealthHabitBuilder />} />
            <Route path="/publications/wealth-building-app" element={<WealthBuildingApp />} />
            <Route path="/publications/family-finance-app" element={<FamilyFinanceApp />} />
            <Route path="/publications/weekly-budget-tracker" element={<WeeklyBudgetTracker />} />
            <Route path="/publications/debt-payoff-tracker" element={<DebtPayoffTracker />} />
            <Route path="/publications/savings-goal-tracker" element={<SavingsGoalTracker />} />
            <Route path="/publications/free-budget-app" element={<FreeBudgetApp />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppRoutes />
      </Router>
    </ThemeProvider>
  );
}
